import { splitUid } from "@/helpers/utility/Id";
import { useImageStore } from "../image/images.store";
import { useElements } from "../element/elements";
import { useModelStore } from "../model/model.store";
import { isSceneElementNode } from "./sceneElementFunctions";
import type { SceneNodeItem, SceneNodeStyle } from "../scene/SceneNodeItem";
import type { ItemStoreEvents } from "../itemStore.store";
import type { Subset } from "@/helpers/utility/Type";
import type { SceneElementData } from "./SceneElementData";

export const elementNodeStyleDefaults: Partial<SceneNodeStyle> = {
  textAlign: "start",
  textColor: null,
  fontSizeRelative: 1,
  fontFamily: null,
  lineHeight: null,
  imageId: null,
  aspectRatioLocked: false,
  fill: null,
  stroke: null,
  strokeWidth: null,
  strokeStyle: null,
  gridLocked: true
};

type ElementAppearanceData = Omit<SceneElementData, "elementUid" | "imageId">;
type ElementAppearanceKey = keyof ElementAppearanceData;
export const defaultElementAppearance: ElementAppearanceData = {
  barVisible: true,
  headlineHidden: false,
  barWidthLimited: false,
  valueVisible: true,
};

export const sceneElementStoreHooks: Partial<ItemStoreEvents<SceneNodeItem<"element">>> = {
  async beforeCreate(item) {
    // Apply default style values for all missing style keys
    for (const key in elementNodeStyleDefaults) {
      const k = key as keyof SceneNodeStyle;
      if (!(k in item) || item[k] === undefined) {
        item[k] = elementNodeStyleDefaults[k] as any;
      }
    }

    // apply default appearance values for missing data keys
    const data = (item.data ?? {}) as Partial<SceneElementData>;
    for (const key in defaultElementAppearance) {
      const k = key as ElementAppearanceKey;
      if (!(k in data) || data[k] === undefined) {
        data[k] = defaultElementAppearance[k] as any;
      }
    }

    return item;
  },

  afterUpdate(sceneNode, changes) {
    // Update default style values
    for (const key in elementNodeStyleDefaults) {
      const k = key as keyof SceneNodeStyle;
      if (k in changes) {
        // if a style key is updated, also update the default value for this key
        // so that new nodes will use the updated value
        elementNodeStyleDefaults[k] = changes[k] as any;
      }
    }

    // Update default appearance values
    if (changes.data) {
      const dataChanges = changes.data as Partial<SceneElementData>;
      for (const key in defaultElementAppearance) {
        const k = key as ElementAppearanceKey;
        if (k in dataChanges) {
          // if an appearance key is updated, also update the default value for this key
          // so that new nodes will use the updated value
          defaultElementAppearance[k] = dataChanges[k] as any;
        }
      }
    }
  },

  async beforeDuplicate(item) {
    const changes: Subset<SceneNodeItem<"element">> = {};
    const items = this.orderedItems as SceneNodeItem[];
    const sameElementNodes = items.filter((sceneNode) => {
      if (sceneNode.sceneId != item.sceneId) return false;
      if (!isSceneElementNode(sceneNode)) return false;
      return sceneNode.data.elementUid === item.data?.elementUid;
    });
    if (sameElementNodes.length > 0) {
      // if there are other nodes using the same element,
      // also duplicate the source element!
      const { type, id } = splitUid(item.data?.elementUid!);
      const elementStore = useModelStore().getStore(type);
      if (elementStore) {
        const sourceElement = elementStore.items[id];

        // Auto-increment the new element's title, adding a number suffix
        // E.g. "Acceptance" -> "Acceptance 2", "Acceptance 2" -> "Acceptance 3", etc. 
        let newElementNumberSuffix = 2;
        const sourceElementNumberSuffixMatch = sourceElement.title.match(/^(.*?)(\d+)$/);
        let baseTitle = sourceElement.title;
        let newElementTitle = `${baseTitle} ${newElementNumberSuffix}`;
        if (sourceElementNumberSuffixMatch) {
          baseTitle = sourceElementNumberSuffixMatch[1];
          newElementNumberSuffix = parseInt(sourceElementNumberSuffixMatch[2]) + 1;
          newElementTitle = `${baseTitle}${newElementNumberSuffix}`;
        }
        // make sure the new element title is unique
        while (elementStore.orderedItems.some((element) => element.title === newElementTitle)) {
          newElementNumberSuffix++;
          newElementTitle = `${baseTitle}${newElementNumberSuffix}`;
        }

        const newElement = await elementStore.duplicate(id, {
          title: newElementTitle
        });
        changes.data = {
          elementUid: newElement.uid
        };
      }
    }
    return changes;
  },

  async afterDelete(sceneNode, store, extraOptions) {
    const promises: Promise<any>[] = [];

    const data = sceneNode.data;

    // delete connected IMAGE
    if (data.imageId != null) {
      const othersUsingSameImage = store.orderedItems.filter((sceneNode: SceneNodeItem) => {
        if (!isSceneElementNode(sceneNode)) return false;
        return sceneNode.data.imageId === data.imageId;
      });
      if (othersUsingSameImage.length === 0) {
        const imageStore = useImageStore();
        promises.push(imageStore.delete({ id: data.imageId, instant: true }));
      }
    }

    // connected ELEMENT
    if (extraOptions?.deleteElement !== false) {
      const elements = useElements();
      const element = elements.byUid[data.elementUid];
      if (element != null) {
        const sameElementNodes = store.orderedItems.filter((sceneNode: SceneNodeItem) => {
          if (!isSceneElementNode(sceneNode)) return false;
          return sceneNode.data.elementUid === data.elementUid;
        });
        if (sameElementNodes.length == 0) {
          // if no other scene node is using the same element, delete the element
          const elementStore = useModelStore().getStore(element.type);
          if (elementStore != null) {
            promises.push(elementStore.delete({ id: element.id, instant: true }));
          }
        }
      }
    }

    return Promise.all(promises);
  }
};
